/**
 * @file   src\components\Onboarding\Dashboard\LeftpanelTab.js
 * @brief  This file is responsible for handling general/match tabs view handling
 * @date   April, 2023
 * @author ZCO Engineer
 * @copyright (c) 2023, ZCO
 */
import React, { useState, createContext, useEffect } from "react";
import LeftpanelTabContent from "./v2/LeftpanelTabContent";
import LeftpanelTabContentRight from "./LeftpanelTabContentRight";
import {
  getLocalStorage,
  getUserType,
  removeLocalStorage,
  setLocalStorage,
} from "../../../helpers/common";
import { isGitchaOwnerView } from "../../../helpers/tenant";
import { useLocation } from "react-router-dom";
import "./LeftpanelTab.scss";

export const filterContext = createContext();
const LeftpanelTab = (props) => {
  const {
    updateFilter,
    filterQuery,
    handleGitchGenieFilter,
    clearFilterGitchaGenie,
    updateFilterGitchaGenie,
    selectedProperty,
    setSelectedProperty,
    setUserLocation,
    gitchaNotAvailble,
    userLocation,
    additionalLocations,
    removeLocationAt,
    checkGitchaAvailableInLoc,
    inDrawer,
    gaugeDemand,
    setGaugeDemand,
    focus,
    onDismiss,
  } = props;

  // The mobile drawer is opened to do one job. Showing sort, location, keyword,
  // a collapsed Filters accordion and the match picker together meant tapping
  // "filters" produced everything except filters.
  const showLocation = !focus || focus === "location";
  const showFilters = !focus || focus === "filters";
  const showMatches = !focus || focus === "matches";

  const location = useLocation();
  const path = location.pathname;

  useEffect(() => {
    // No property is carried across sessions - the feed always opens on the
    // full market until the agent picks a property.
    removeLocalStorage("selectedPropertyId");
  }, []);
  const [deepLinkedToMatch, setDeepLinkedToMatch] = useState(
    () => window.location.pathname === "/match"
  );

  useEffect(() => {
    if (path === "/match") {
      // Match deep-link from an email still lands on the demand view.
      setDeepLinkedToMatch(true);
      removeLocalStorage("selectedPropertyId");
      removeLocalStorage("MatchSelectedClient");
    } else {
      setDeepLinkedToMatch(false);
    }
  }, [path]);

  // gaugeDemand/setGaugeDemand are owned by Home and passed down: this
  // component mounts twice (drawer + rail), and local state here previously
  // reset to false on the second mount, silently exiting match mode the
  // instant the filter drawer opened.

  // Choosing a market means they want the market, not a property, so the
  // selection clears and the feed returns to browsing. Without this the panel
  // was a dead end: no way back except a link.
  const committedLocation = userLocation?.formatted_address;
  const prevLocation = React.useRef(committedLocation);
  useEffect(() => {
    if (prevLocation.current !== committedLocation) {
      prevLocation.current = committedLocation;
      if (committedLocation) {
        setGaugeDemand(false);
        setSelectedProperty(null);
      }
    }
  }, [committedLocation]);
  const isMatchMode = gaugeDemand || deepLinkedToMatch;
  // The same rail serves agents and property owners with identical controls,
  // so only the labels change. Not "is a member" - this is also the
  // signed-out page, where getUserType() is null.
  const isMember = isGitchaOwnerView(getUserType());

  useEffect(() => {
    handleGitchGenieFilter(isMatchMode);
    setLocalStorage("FilterTabKey", isMatchMode ? "match" : "General");
  }, [isMatchMode]);

  return (
    <>
      {!focus && (
        <div className="feed-panel-intro">
          <h2>
            {isMember ? "Buyer want-listings near you" : "Search Active Buyer Demand"}
          </h2>
          <p>
            {isMember
              ? "Posted by agents for their buyer clients."
              : "Want-Listings from agents in your network."}
          </p>
        </div>
      )}

      {/* Location sets the market and belongs with the feed context, so it
          stays in the rail. Keyword and the filter controls live in the Sort
          and Filter panel above the feed. */}
      {/* Every filter group expanded is right in the desktop rail, which is
          tall. On a phone it makes Property type alone twenty checkboxes deep
          and turns the sheet into several screens of scrolling before the agent
          can even see which filters exist - so the drawer collapses them to
          their headers, which already carry a count. */}
      <div hidden={!showLocation && !showFilters}>
        <LeftpanelTabContent
          locationOnly={focus ? focus === "location" : !inDrawer}
          filtersOnly={focus === "filters"}
          collapsedSections={focus === "filters"}
          inDrawer={inDrawer}
          updateFilter={updateFilter}
          filterQuery={filterQuery}
          setUserLocation={setUserLocation}
          userLocation={userLocation}
          additionalLocations={additionalLocations}
          removeLocationAt={removeLocationAt}
          gitchaNotAvailble={gitchaNotAvailble}
          checkGitchaAvailableInLoc={checkGitchaAvailableInLoc}
        />
      </div>


      <div className="feed-panel-matching" hidden={!showMatches}>
        <h3 className="feed-section-title">
          {isMatchMode
            ? isMember
              ? "Want-listings matching your home"
              : "Matches for this property"
            : isMember
              ? "Your matches"
              : "Matches"}
        </h3>
        <p className="feed-section-note">
          {isMember
            ? "Want-listings whose criteria match your home."
            : "See which Want-Listings match your MLS listings or prospects' properties."}
        </p>
        <LeftpanelTabContentRight
          gaugeDemand={gaugeDemand}
          setGaugeDemand={setGaugeDemand}
          onDismiss={onDismiss}
          clearFilterGitchaGenie={clearFilterGitchaGenie}
          updateFilterGitchaGenie={updateFilterGitchaGenie}
          selectedProperty={selectedProperty}
          setSelectedProperty={setSelectedProperty}
        />
      </div>
    </>
  );
};
export default LeftpanelTab;
